import { BorderGridContainer, Button, GridBox, H2, P, Slider, Spinner } from '@ahmdigital/ui';
import { forEach, isNull, keys, omitBy, toLower } from 'lodash/fp';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import formatCurrency from '@ahmdigital/logic/lib/claim/format-currency';
import moment from 'moment';

import { CustomerState } from '../../../../types/state/customer';
import { marks, percentages, productId, scale, staticFrequency } from './constants';
import { RootState } from '../../../../reducers';
import config from '../../../../config';
import constants from '../../../../ahm-constants';
import getDateOfBirthFromAge from '../../../../utils/get-date-of-birth-from-age';
import getYouthDiscountSavings from '../../utils/get-youth-discount-savings';
import PremiumReviewToggle from '../../../../components/premium-review-toggle-wrapper';
import PriceActions from '../../../../actions/price';
import ProductActions from '../../../../actions/product';
import selectCustomer from '../../../../selectors/select-customer';
import styles from './styles.module.sass';
import tipFormatter from './components/tip-formatter';

const ages = keys(percentages);
const getFutureEffectiveDate = moment(config.get('features:premiumChange:futureEffectiveDate')).format(
  constants.DATE_FORMAT.ISO_8601,
);

const getDefaultCustomerPriceData = (customer: Partial<CustomerState>, age: number) => {
  // We force price request data to be consistent, ignoring what
  // the user might have in redux state
  const output: Partial<CustomerState> = {
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '"0"' is not assignable to type '"BRACKET_0" | "BRACKET_1" | "BRACKET_2"'.
    ageBracket: constants.REBATE_AGE_BRACKET.BRACKET_0,
    frequency: staticFrequency,
    incomeTier: constants.INCOME_TIER.TIER_0,
    incomeType: constants.INCOME_TYPE.SINGLE,
    rebate: false,
    scale,
    state: customer.state,
  };
  // @ts-expect-error - Automatic, Please fix when editing this file
  if (percentages[age] > 0) {
    output.members = [
      {
        continuous: true,
        dob: getDateOfBirthFromAge(age),
        relationship: 'client',
      },
    ];
  }
  return output;
};

const getPriceOptions = (useFutureEffectiveDate: boolean) => {
  const futureEffectiveDate = useFutureEffectiveDate ? getFutureEffectiveDate : null;
  return omitBy(isNull, {
    adjustedScale: true,
    date: futureEffectiveDate,
    lhc: false,
  });
};

const YouthDiscountSlider = () => {
  const [age, setAge] = useState(18);
  const dispatch = useDispatch();
  const customer = useSelector(selectCustomer);
  const prices = useSelector((state: RootState) => state.price.data);
  const products = useSelector((state: RootState) => state.product.data);
  const useFutureEffectiveDate = useSelector((state: RootState) => state.price.useFutureEffectiveDate);

  const product = products?.[productId];

  useEffect(() => {
    if (!product) {
      dispatch(ProductActions.load(productId));
    }
  }, [dispatch, product]);

  useEffect(() => {
    forEach((discountAge: string) => {
      dispatch(
        PriceActions.load(
          productId,
          { customer: getDefaultCustomerPriceData(customer, Number(discountAge)) },
          getPriceOptions(useFutureEffectiveDate),
        ),
      );
    }, ages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useFutureEffectiveDate]);

  if (!product) {
    return null;
  }

  const customerData = getDefaultCustomerPriceData(customer, age);
  const savings = getYouthDiscountSavings({
    customer: customerData,
    frequency: staticFrequency,
    priceOptions: getPriceOptions(useFutureEffectiveDate),
    prices,
    productId,
  });

  return (
    <BorderGridContainer>
      <GridBox padding="4rem">
        <div className={styles.ageSelectionBox}>
          <H2 variant="marginBottomSpacer9">How much can I save on my hospital cover?</H2>
          <PremiumReviewToggle type={constants.PREMIUM_REVIEW_TOGGLE_STYLE.ALTERNATIVE_PAGE} />
          <P>Select your age below to begin</P>
          {isNull(savings) ? (
            <Spinner variant={styles.spinner} />
          ) : (
            <>
              <div className={styles.slider}>
                <Slider
                  analytics={{ action: 'on_youth_discount_age_move_slider', component: 'youth_discount' }}
                  onChange={(newAge) => setAge(newAge as number)}
                  marks={marks}
                  min={18}
                  max={30}
                  tipFormatter={tipFormatter}
                  ariaLabelForHandle="discount"
                />
                <H2 looksLike="h4" variant={['marginBottomSpacer7', styles.results]}>
                  {savings === 0 ? (
                    'You don’t qualify for a discount, but we still have affordable cover available.'
                  ) : (
                    <>
                      {/* @ts-expect-error [🤖 PLEASE FIX 🤖]: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ "australian-capital-territory": string; "new-south-wales": string; queensland: string; "south-australia": string; tasmania: string; victoria: string; "western-australia": string; "northern-territory": string; }'. */}
                      If you’re {toLower(scale)} in {constants.STATE_LABEL[customerData.state]} you could save up to{' '}
                      {formatCurrency(savings)} a year on {product.name} product<sup>*</sup>
                    </>
                  )}
                </H2>
              </div>
              {savings > 0 && (
                <P variant={['marginBottomSpacer7', styles.disclaimer]} data-testid="disclaimer">
                  <sup>*</sup>Paying by direct debit. Australian Government Rebate is not applied. <br />
                  Hospital price includes an excess of $750 and excludes Lifetime Health Cover loading. <br />
                  Youth Discount only applies to hospital component.
                </P>
              )}
              <Button variant="sharp" to={constants.URLS.SALES.PRODUCT_LISTING_HOSPITAL}>
                View hospital cover
              </Button>
            </>
          )}
        </div>
      </GridBox>
    </BorderGridContainer>
  );
};

export default YouthDiscountSlider;
